// Exams a learner can be given access to. `id` is question_master.exam_id / user_access.exam_ids.
export interface ExamInfo {
  id: number;
  key: string;
  name: string;
  short: string;
  emoji: string;
  program?: { path: string; label: string }; // learning/program page for this exam, when one exists
}

export const EXAM_CATALOG: ExamInfo[] = [
  { id: 1, key: 'saa', name: 'AWS Solutions Architect Associate (SAA-C03)', short: 'AWS SAA-C03', emoji: '🏗️', program: { path: '/aws-saa-c03', label: 'AWS Solutions Architect Associate' } },
  { id: 2, key: 'sap', name: 'AWS Solutions Architect Professional (SAP-C02)', short: 'AWS SAP-C02', emoji: '🎓', program: { path: '/aws-sap', label: 'AWS Solutions Architect Professional' } },
  { id: 3, key: 'ccp', name: 'AWS Cloud Practitioner (CLF-C02)', short: 'AWS CLF-C02', emoji: '☁️', program: { path: '/aws-ccp', label: 'AWS Cloud Practitioner' } },
  { id: 4, key: 'dva', name: 'AWS Developer Associate (DVA-C02)', short: 'AWS DVA-C02', emoji: '💻', program: { path: '/aws-dva-c02', label: 'AWS Developer Associate' } },
  { id: 5, key: 'sysdesign', name: 'System Design', short: 'System Design', emoji: '⚙️' },
  { id: 6, key: 'dsa', name: 'Data Structures & Algorithms', short: 'DSA', emoji: '🧩' },
  { id: 7, key: 'python', name: 'Python', short: 'Python', emoji: '🐍' },
  { id: 8, key: 'dotnet', name: '.NET / Java', short: '.NET / Java', emoji: '🔷' },
  { id: 9, key: 'aiml', name: 'AI & Machine Learning', short: 'AI / ML', emoji: '🤖' },
];

export const getExamById = (id: number): ExamInfo | undefined => EXAM_CATALOG.find(e => e.id === id);
export const getExamByKey = (key: string): ExamInfo | undefined => EXAM_CATALOG.find(e => e.key === key);

// Accounts created before exams could be assigned (no exam_ids) keep the original behaviour: SAP-C02 only.
export const LEGACY_DEFAULT_EXAM_IDS = [2];

/** "1,2" | [1,2] | null  →  sorted unique ids (null when nothing is stored). */
export const parseExamIds = (raw: unknown): number[] | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const parts = Array.isArray(raw) ? raw : String(raw).split(',');
  const ids = parts.map(p => parseInt(String(p).trim(), 10)).filter(n => !isNaN(n));
  return ids.length ? Array.from(new Set(ids)).sort((a, b) => a - b) : null;
};

export const formatExamIds = (ids: number[]): string => Array.from(new Set(ids)).sort((a, b) => a - b).join(',');

const SESSION_KEY = 'userExams';

export const saveSessionExamIds = (ids: number[]) => sessionStorage.setItem(SESSION_KEY, JSON.stringify(ids));
export const clearSessionExamIds = () => { sessionStorage.removeItem(SESSION_KEY); sessionStorage.removeItem('userSetLimit'); };
const SET_LIMIT_KEY = 'userSetLimit';
/** user_access.no_of_sets: how many sets (from the lowest set number) the learner may open; null = no limit. */
export const saveSessionSetLimit = (n: number | null) => {
  if (n && n > 0) sessionStorage.setItem(SET_LIMIT_KEY, String(n)); else sessionStorage.removeItem(SET_LIMIT_KEY);
};
export const getSessionSetLimit = (): number | null => {
  const n = parseInt(sessionStorage.getItem(SET_LIMIT_KEY) || '', 10);
  return !isNaN(n) && n > 0 ? n : null;
};
/** The set numbers a learner may open: the first `limit` of the given set numbers (all when no limit). */
export const allowedSetNumbers = (setNumbers: number[], limit: number | null = getSessionSetLimit()): number[] => {
  const sorted = [...setNumbers].sort((a, b) => a - b);
  return limit ? sorted.slice(0, limit) : sorted;
};

export const getSessionExamIds = (): number[] => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
    if (Array.isArray(parsed)) return parsed.filter((n: unknown) => typeof n === 'number') as number[];
  } catch { /* fall through */ }
  return LEGACY_DEFAULT_EXAM_IDS;
};
